import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../theme/ThemeContext';
import type { Appointment, Doctor } from '../types';
import { getAppointmentBlockColor, getReadableTextColor, isSameDay } from '../utils';

const MAX_CHIPS = 3;

interface PhoneMonthViewProps {
  weeks: Date[][];
  monthDate: Date;
  nowTick: number;
  doctors: Doctor[];
  getAppointments: (day: Date) => Appointment[];
  getPatientName: (app: Appointment) => string;
  onDayClick: (day: Date) => void;
}

export const PhoneMonthView: React.FC<PhoneMonthViewProps> = ({ weeks, monthDate, nowTick, doctors, getAppointments, getPatientName, onDayClick }) => {
  const { i18n } = useTranslation();
  const { tokens } = useTheme();
  const chrome = tokens.calendar;
  const today = new Date(nowTick);
  const weekdayLabels = weeks[0]?.map(day => day.toLocaleDateString(i18n.language, { weekday: 'narrow' })) ?? [];

  return (
    <div className="phone-cal-month" data-testid="phone-month-view" style={{ background: chrome.panel, color: chrome.text }}>
      <div className="phone-cal-month-weekdays" style={{ color: chrome.muted, borderBottom: `1px solid ${chrome.divider}` }}>
        {weekdayLabels.map((label, index) => <span key={index}>{label}</span>)}
      </div>
      <div className="phone-cal-month-grid" style={{ gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))` }}>
        {weeks.flat().map(day => {
          const inMonth = day.getMonth() === monthDate.getMonth();
          const isToday = isSameDay(day, today);
          const apps = getAppointments(day);
          const hidden = apps.length - MAX_CHIPS;
          return (
            <button
              key={day.toDateString()}
              type="button"
              className="phone-cal-month-cell"
              data-testid={`phone-month-day-${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`}
              onClick={() => onDayClick(day)}
              style={{ borderColor: chrome.divider, color: inMonth ? chrome.text : chrome.muted }}
            >
              <span
                className="phone-cal-month-daynum"
                style={isToday ? { background: tokens.app.primary, color: tokens.app.primaryText } : undefined}
              >
                {day.getDate()}
              </span>
              {apps.slice(0, hidden > 0 ? MAX_CHIPS - 1 : MAX_CHIPS).map(app => {
                const color = getAppointmentBlockColor(app, doctors);
                return (
                  <span
                    key={app.id}
                    className="phone-cal-month-chip"
                    style={{ background: color, color: getReadableTextColor(color), opacity: inMonth ? 1 : 0.6 }}
                  >
                    {getPatientName(app)}
                  </span>
                );
              })}
              {hidden > 0 && (
                <span className="phone-cal-month-more" style={{ color: chrome.muted }}>
                  +{hidden + 1}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
